import React, { useState, useEffect } from "react";
import DocumentUpload from "../../components/DocumentUpload";
import LocationSelectors from "../../components/LocationSelectors";
import { states, districts } from "../../data/locationData";
import { useNavigate, useLocation } from "react-router-dom";
import "../../styles/FormPages.css";

const DepartmentFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { country, state: stateVal, district, lawCategory } = location.state || {};

  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;

  const [formData, setFormData] = useState({
    country: country || "",
    state: stateVal || "",
    district: district || "",

    applicantFullName: "",
    applicantFatherOrHusband: "",
    applicantAge: "",
    applicantGender: "",
    applicantOccupation: "",
    applicantContact: "",
    applicantEmail: "",
    applicantAddress: "",
    complainantAgainst: "",
    complainantRelationship: "",
    complainantRelationshipOther: "",
    complainantRole: "",
    complaintTitle: "",
    complaintTitleOther: "",
    complaintDescription: "",
    complaintDetails: "",
    reliefSought: "",
    departmentName: "",
    departmentDistrict: district || "",
    departmentState: stateVal || "",
    isUrgent: "No",
    previousApplication: "No",
    declaration: false,
  });

  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [extractedContent, setExtractedContent] = useState({});

  const handleDocumentsChange = (files, content) => {
    setUploadedDocuments(files);
    setExtractedContent(content);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleDepartmentStateSelect = (e) => {
    const nextState = e.target.value;
    setFormData((prev) => ({
      ...prev,
      departmentState: nextState,
      departmentDistrict: "",
    }));
  };

  const handleDepartmentDistrictSelect = (e) => {
    setFormData((prev) => ({
      ...prev,
      departmentDistrict: e.target.value,
    }));
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const selectedCountry = formData.country || country || "";
    const selectedState = formData.state || stateVal || "";
    const selectedDistrict = formData.district || district || "";
    
    let detailsText = `
👤 Applicant Details:
Full Name: ${formData.applicantFullName}
Father's/Husband's Name: ${formData.applicantFatherOrHusband}
Age: ${formData.applicantAge}
Gender: ${formData.applicantGender}
Occupation: ${formData.applicantOccupation}
Contact: ${formData.applicantContact}
Email: ${formData.applicantEmail}
Address: ${formData.applicantAddress}

👥 Complaint Against:
Name: ${formData.complainantAgainst}
Relationship: ${formData.complainantRelationshipOther || formData.complainantRelationship}
Role: ${formData.complainantRole}

📋 Complaint Details:
Title: ${formData.complaintTitleOther || formData.complaintTitle}
Description: ${formData.complaintDescription}
Detailed Complaint: ${formData.complaintDetails}
Relief Sought: ${formData.reliefSought}

🏢 Department Details:
Department Name: ${formData.departmentName}
District: ${formData.departmentDistrict}
State: ${formData.departmentState}
Is Urgent: ${formData.isUrgent}
Previous Application: ${formData.previousApplication}
`.trim();

    if (uploadedDocuments.length > 0) {
      detailsText += `\n\n📄 Supporting Documents:`;
      uploadedDocuments.forEach((doc, idx) => {
        detailsText += `\n\nDocument ${idx + 1}: ${doc.name}`;
        if (extractedContent[doc.id]) {
          detailsText += `\nExtracted Content:\n${extractedContent[doc.id]}`;
        }
      });
    }

    navigate("/drafts", {
      state: {
        query: detailsText,
        summary: detailsText,
        country: selectedCountry,
        state: selectedState,
        district: selectedDistrict,
        lawCategory,
  draftType: "department",
         includeSections: ["department"],
  entityName: formData.applicantFullName,
  uploadedDocuments,
  extractedContent,
      },
    });
  };

  const renderStep1 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        👤 Applicant Details
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Full Name *</label>
          <input
            type="text"
            name="applicantFullName"
            value={formData.applicantFullName}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Father's/Husband's Name *</label>
          <input
            type="text"
            name="applicantFatherOrHusband"
            value={formData.applicantFatherOrHusband}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Age *</label>
          <input
            type="number"
            name="applicantAge"
            value={formData.applicantAge}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Gender *</label>
          <select
            name="applicantGender"
            value={formData.applicantGender}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Occupation *</label>
          <input
            type="text"
            name="applicantOccupation"
            value={formData.applicantOccupation}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Contact Number *</label>
          <input
            type="tel"
            name="applicantContact"
            value={formData.applicantContact}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Email ID</label>
          <input
            type="email"
            name="applicantEmail"
            value={formData.applicantEmail}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-page-col">
          <label>Address *</label>
          <textarea
            name="applicantAddress"
            value={formData.applicantAddress}
            onChange={handleInputChange}
            rows={2}
            required
          />
        </div>
      </div>

      <LocationSelectors
        country={formData.country}
        state={formData.state}
        district={formData.district}
        onChange={(next) => setFormData((prev) => ({ ...prev, ...next }))}
      />
    </div>
  );

  const renderStep2 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        👥 Complaint Against
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Person/Entity Name *</label>
          <input
            type="text"
            name="complainantAgainst"
            value={formData.complainantAgainst}
            onChange={handleInputChange}
            placeholder="Name of person, officer, or entity you are complaining against"
            required
          />
        </div>
        <div className="form-page-col">
          <label>Relationship to Applicant *</label>
          <select
            name="complainantRelationship"
            value={formData.complainantRelationship}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Relationship</option>
            <option value="Government Officer">Government Officer</option>
            <option value="Department Employee">Department Employee</option>
            <option value="Public Servant">Public Servant</option>
            <option value="Contractor">Contractor</option>
            <option value="Service Provider">Service Provider</option>
            <option value="Unknown Person">Unknown Person</option>
            <option value="Organization">Organization</option>
            <option value="Institution">Institution</option>
            <option value="Private Company">Private Company</option>
            <option value="Other">Other (specify)</option>
          </select>
        </div>
      </div>
      {formData.complainantRelationship === "Other" && (
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Specify Relationship *</label>
            <input
              type="text"
              name="complainantRelationshipOther"
              value={formData.complainantRelationshipOther}
              onChange={handleInputChange}
              required
            />
          </div>
        </div>
      )}
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Role/Position *</label>
          <select
            name="complainantRole"
            value={formData.complainantRole}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Role</option>
            <option value="District Collector">District Collector</option>
            <option value="Tehsildar">Tehsildar</option>
            <option value="Block Development Officer">Block Development Officer</option>
            <option value="Village Revenue Officer">Village Revenue Officer</option>
            <option value="Police Officer">Police Officer</option>
            <option value="Municipal Officer">Municipal Officer</option>
            <option value="Panchayat Secretary">Panchayat Secretary</option>
            <option value="Sub-Registrar">Sub-Registrar</option>
            <option value="Revenue Inspector">Revenue Inspector</option>
            <option value="Health Officer">Health Officer</option>
            <option value="Education Officer">Education Officer</option>
            <option value="Transport Officer">Transport Officer</option>
            <option value="Forest Officer">Forest Officer</option>
            <option value="Labour Officer">Labour Officer</option>
            <option value="Unknown">Unknown</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        📋 Complaint Details
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Complaint Category *</label>
          <select
            name="complaintTitle"
            value={formData.complaintTitle}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Complaint Category</option>
            <option value="Corruption/Bribery">Corruption/Bribery</option>
            <option value="Delay in Service">Delay in Service</option>
            <option value="Denial of Service">Denial of Service</option>
            <option value="Poor Service Quality">Poor Service Quality</option>
            <option value="Harassment">Harassment</option>
            <option value="Discrimination">Discrimination</option>
            <option value="Misuse of Power">Misuse of Power</option>
            <option value="Negligence">Negligence</option>
            <option value="Procedural Violation">Procedural Violation</option>
            <option value="Document Related">Document Related</option>
            <option value="Revenue Related">Revenue Related</option>
            <option value="License/Permit Issue">License/Permit Issue</option>
            <option value="Welfare Scheme Issue">Welfare Scheme Issue</option>
            <option value="Infrastructure Problem">Infrastructure Problem</option>
            <option value="Other">Other (specify)</option>
          </select>
        </div>
        <div className="form-page-col">
          {formData.complaintTitle === "Other" && (
            <>
              <label>Specify Complaint Category *</label>
              <input
                type="text"
                name="complaintTitleOther"
                value={formData.complaintTitleOther}
                onChange={handleInputChange}
                required
              />
            </>
          )}
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Brief Description *</label>
          <textarea
            name="complaintDescription"
            value={formData.complaintDescription}
            onChange={handleInputChange}
            rows={3}
            placeholder="Brief summary of your complaint"
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Detailed Complaint *</label>
          <textarea
            name="complaintDetails"
            value={formData.complaintDetails}
            onChange={handleInputChange}
            rows={5}
            placeholder="Provide detailed facts, dates, incidents, and circumstances"
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Relief Sought *</label>
          <textarea
            name="reliefSought"
            value={formData.reliefSought}
            onChange={handleInputChange}
            rows={3}
            placeholder="What action or relief do you want from the department?"
            required
          />
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <>
      <div className="form-page-section">
        <div className="form-page-section-title">
          🏢 Department Details
        </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Department Name *</label>
            <select
              name="departmentName"
              value={formData.departmentName}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Department</option>
              <option value="Revenue Department">Revenue Department</option>
              <option value="Police Department">Police Department</option>
              <option value="Municipal Corporation">Municipal Corporation</option>
              <option value="Panchayati Raj Department">Panchayati Raj Department</option>
              <option value="Education Department">Education Department</option>
              <option value="Health Department">Health Department</option>
              <option value="Transport Department">Transport Department</option>
              <option value="Forest Department">Forest Department</option>
              <option value="Labour Department">Labour Department</option>
              <option value="Social Welfare Department">Social Welfare Department</option>
              <option value="Agriculture Department">Agriculture Department</option>
              <option value="Public Works Department">Public Works Department</option>
              <option value="Registration Department">Registration Department</option>
              <option value="Electricity Board">Electricity Board</option>
              <option value="Water Supply Department">Water Supply Department</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div className="form-page-col">
            <label>Department District *</label>
            {(() => {
              const hasStateList = (states[formData.country] || []).length > 0;
              const departmentDistrictOptions = districts[formData.departmentState] || [];

              if (!hasStateList) {
                return (
                  <input
                    type="text"
                    name="departmentDistrict"
                    value={formData.departmentDistrict}
                    onChange={handleInputChange}
                    required
                  />
                );
              }

              if (formData.departmentState && departmentDistrictOptions.length === 0) {
                return (
                  <input
                    type="text"
                    name="departmentDistrict"
                    value={formData.departmentDistrict}
                    onChange={handleInputChange}
                    required
                  />
                );
              }

              return (
                <select
                  name="departmentDistrict"
                  value={formData.departmentDistrict}
                  onChange={handleDepartmentDistrictSelect}
                  required
                  disabled={!formData.departmentState}
                >
                  <option value="">Select District</option>
                  {departmentDistrictOptions.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              );
            })()}
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Department State *</label>
            {(states[formData.country] || []).length > 0 ? (
              <select
                name="departmentState"
                value={formData.departmentState}
                onChange={handleDepartmentStateSelect}
                required
                disabled={!formData.country}
              >
                <option value="">Select State</option>
                {(states[formData.country] || []).map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                name="departmentState"
                value={formData.departmentState}
                onChange={handleInputChange}
                required
              />
            )}
          </div>
          <div className="form-page-col">
            <label>Is this Urgent? *</label>
            <select
              name="isUrgent"
              value={formData.isUrgent}
              onChange={handleInputChange}
              required
            >
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </select>
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Previous Application Filed? *</label>
            <select
              name="previousApplication"
              value={formData.previousApplication}
              onChange={handleInputChange}
              required
            >
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </select>
          </div>
        </div>
      </div>
      <div className="form-page-declaration">
        <div className="form-page-section-title">
          ✅ Declaration
        </div>
        <label>
          <input
            type="checkbox"
            name="declaration"
            checked={formData.declaration}
            onChange={handleInputChange}
            required
          />
          <span>I declare that all the information provided is true and I understand the legal implications *</span>
        </label>
      </div>
    </>
  );

  return (
    <div className="form-page-container">
      <div className="form-page-header">
        <a href="#" onClick={() => navigate(-1)} className="form-page-back-link">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Draft Selection
        </a>
        <h1 className="form-page-title">File Department Complaint</h1>
        <p className="form-page-subtitle">Fill in the details below to generate a professional department complaint</p>
      </div>

      <div className="form-page-content">
        <form onSubmit={handleSubmit}>
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}

          {currentStep === totalSteps && (
            <div className="form-page-section">
              <div className="form-page-section-title">📄 Supporting Documents</div>
              <p className="form-page-note">
                Upload previous applications, correspondence, receipts, or other supporting documents. Extracted text will be added to your department complaint draft.
              </p>
              <DocumentUpload
                onDocumentsChange={handleDocumentsChange}
                maxFiles={8}
                acceptedTypes=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
              />
            </div>
          )}

          <div className="form-page-navigation">
            <div className="form-page-step-indicator">
              Step {currentStep} of {totalSteps}
            </div>
            <div className="form-page-buttons">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="form-page-btn form-page-btn-secondary"
                >
                  Previous
                </button>
              )}
              {currentStep < totalSteps ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="form-page-btn form-page-btn-primary"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  className="form-page-btn form-page-btn-primary"
                >
                  Generate Department Complaint
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DepartmentFormPage;
